import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Company = Database['public']['Tables']['companies']['Row'];

interface ShopContextType {
  shops: Company[];
  activeShopId: string | null;
  activeShop: Company | null;
  setActiveShopId: (shopId: string) => void;
  loadShops: (userId: string) => Promise<void>;
  isLoading: boolean;
}

export const ShopContext = createContext<ShopContextType | undefined>(undefined);

const STORAGE_KEY = 'lc_active_shop';

export function ShopProvider({ children }: { children: React.ReactNode }) {
  const [shops, setShops] = useState<Company[]>([]);
  const [activeShopId, setActiveShopIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load shops for a user (called by bootstrap and ShopInitializer on auth changes)
  const loadShops = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('owner_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setShops(data || []);

      // Set active shop from localStorage or first shop
      const savedShopId = localStorage.getItem(STORAGE_KEY);
      const shopToActivate = savedShopId && data?.find(s => s.id === savedShopId)
        ? savedShopId
        : data?.[0]?.id || null;

      if (shopToActivate) {
        setActiveShopIdState(shopToActivate);
        localStorage.setItem(STORAGE_KEY, shopToActivate);
      }
    } catch (error) {
      console.error('Error loading shops:', error);
    }
  }, []);

  // Bootstrap: resolve auth once, then load shops (prevents infinite "Loading dashboard...")
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        setIsLoading(true);
        const { data: { user }, error } = await supabase.auth.getUser();
        if (cancelled) return;
        if (error) {
          console.warn('[ShopContext] Failed to get user:', error);
        }
        if (!user) {
          setShops([]);
          setActiveShopIdState(null);
          return;
        }

        await loadShops(user.id);
      } catch (e) {
        console.error('[ShopContext] init failed:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [loadShops]);

  // Set active shop and persist to localStorage

  const setActiveShopId = useCallback((shopId: string) => {
    setActiveShopIdState(shopId);
    localStorage.setItem(STORAGE_KEY, shopId);
  }, []);

  // Get active shop object
  const activeShop = shops.find(shop => shop.id === activeShopId) || null;

  return (
    <ShopContext.Provider
      value={{
        shops,
        activeShopId,
        activeShop,
        setActiveShopId,
        loadShops,
        isLoading,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
}
